// Copyright (c) 2021, Picsa Advisory Services (Pty) Ltd. All rights reserved.
import gql from 'graphql-tag'
import React, { createRef, PureComponent } from 'react'
import PropTypes from 'prop-types'
import { Mutation } from 'react-apollo'

import {
  TabbedView,
  WithdrawalCard,
  ObjectCases,
  ObjectFiles,
  SvgIcon,
  WithdrawalAddEditModal as EditModal,
  WithdrawalBankChangeModal as BankChangeModal,
  SpanWithData,
  Modal
} from '../'

import { hasResponsibility } from '../../utils'

import { Actions } from './Actions'
import { CancelModal } from './CancelModal'

import { MUTATION_WITHDRAWAL, WITHDRAWAL_BANKACCOUNT_UPDATE } from '../../mutations'

const modalRef = createRef()

export class WithdrawalDetail extends PureComponent {
  static propTypes = {
    withdrawal: PropTypes.object.isRequired,
    identityId: PropTypes.string.isRequired,
    openTab: PropTypes.number.isRequired,
    className: PropTypes.string
  }

  state = {
    numberOfCases: 0,
    numberOfFiles: 0,
    edit: false,
    cancel: false,
    bankChange: false,
    showModalErrorMessage: null,  // if set to string value, the value will be displayed in modal
  }

  componentDidMount() {
    document.addEventListener('mousedown', this.handleClickOutsideModal)
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutsideModal)
  }

  handleClickOutsideModal = (e) => {
    if (this.state.showModalErrorMessage && modalRef.current && !modalRef.current.contains(e.target)) {
      // click is outside, close modal
      this.setState({ showModalErrorMessage: false })
    }
  }

  toggleEditModal = () => (
    this.setState(prevState => ({ edit: !prevState.edit }))
  )

  toggleCancelModal = () => (
    this.setState(prevState => ({ cancel: !prevState.cancel }))
  )

  toggleBankChangeModal = () => (
    this.setState(prevState => ({ bankChange: !prevState.bankChange }))
  )

  toggleErrorModal = (message) => {
    if (message) {
      this.setState({ showModalErrorMessage: message })
    }
    else {
      this.setState({ showModalErrorMessage: null })
    }
  }

  saveChanges = (withdrawalMutate, accountId, targetType, targetId, initiateOn, amountRequested, amountType, productMetaDataObj) => {
    let input = {
      id: this.props.withdrawal.id,
      account: accountId,
      initiateOn,
      amountRequested,
      amountType: amountType.toLowerCase()
    }

    if (targetType === 'product') {
      input.targetProduct = targetId
      input.targetBankaccount = "null"
      if (this.props.withdrawal.targetProduct &&
        this.props.withdrawal.targetProduct.id !== targetId) {
        input.metadata = JSON.stringify(productMetaDataObj)
      }
    }

    if (targetType === 'bankaccount') {
      input.targetBankaccount = targetId
      input.targetProduct = "null"
      if (this.props.withdrawal.targetProduct) {
        input.metadata = JSON.stringify({})
      }
    }

    withdrawalMutate({ variables: { input } })
    .then(this.toggleEditModal)
    .catch(this.handleWithdrawalSaveFail)
  }

  saveBankChange = (bankaccountUpdate, targetBankaccountId) => {
    const input = {
      withdrawalId: this.props.withdrawal.id,
      identitybankaccountId: targetBankaccountId,
    }

    bankaccountUpdate({ variables: { input } })
    .then(this.toggleBankChangeModal)
    .catch(this.handleBankUpdateFail)
  }

  handleWithdrawalSaveFail = (error) => {
    this.toggleEditModal()
    if (error.hasOwnProperty('graphQLErrors') && error.graphQLErrors.length > 0) {
      this.toggleErrorModal(error.graphQLErrors[0].message)
    }
  }

  handleBankUpdateFail = (error) => {
    this.toggleBankChangeModal()
    if (error.hasOwnProperty('graphQLErrors') && error.graphQLErrors.length > 0) {
      this.toggleErrorModal(error.graphQLErrors[0].message)
    }
  }

  saveCancellation = (withdrawalMutate, cancelledBy) => {
    withdrawalMutate({
      variables: {
        input: {
          id: this.props.withdrawal.id,
          status: cancelledBy
        }
      }
    })
    .then(this.toggleCancelModal)
    .catch(error => console.error(error))
  }

  render() {
    const {
      id,
      account,
      amountRequested,
      amountType,
      feeAmount,
      initiateOn,
      status,
      withdrawalAmount,
      client,
      feePayment,
      sellTransaction,
      targetBankaccount,
      targetProduct,
      withdrawalbatch,
      withdrawalPayment,
      userMayUpdate,
      bankaccountIsUpdateable,
      bankaccountNotUpdateableReason
    } = this.props.withdrawal

    const { edit, cancel, bankChange, showModalErrorMessage, numberOfCases, numberOfFiles } = this.state

    const userMayOverrideDate = hasResponsibility('self', 'override_withdrawal_date')

    return (
      <div className={`slds-size_1-of-1 ${this.props.className ? this.props.className : ''}`}>
        {/* overview section */}
        <div className='slds-p-around_large slds-border_bottom'>
          <div className='slds-grid'>
            <WithdrawalCard
              withdrawalId={id}
              identityId={client.identity.id}
              amountRequested={amountRequested}
              amountType={amountType}
              feeAmount={feeAmount}
              initiateOn={initiateOn}
              status={status}
              withdrawalAmount={withdrawalAmount}
              feePayment={feePayment}
              sellTransaction={sellTransaction}
              sourceName={account.description}
              targetName={targetProduct
                ? targetProduct.name
                : <span>
                    {targetBankaccount.bankaccount.accountNumber} (
                    <SpanWithData
                      forField='BankAccountBankName'
                      searchKey={targetBankaccount.bankaccount.bankName}
                    />
                    )
                  </span>
              }
              withdrawalBatch={withdrawalbatch}
              withdrawalPayment={withdrawalPayment}
              detailView={true}
              className='slds-size_1-of-2'
            />
            <Actions
              userMayUpdate={userMayUpdate}
              bankaccountIsUpdateable={bankaccountIsUpdateable}
              bankaccountNotUpdateableReason={bankaccountNotUpdateableReason}
              onEditClick={this.toggleEditModal}
              onCancelClick={this.toggleCancelModal}
              onBankChangeClick={this.toggleBankChangeModal}
            />
          </div>
        </div>
        {/* detail section */}
        <TabbedView
          tabs={[
            `Cases for this withdrawal${numberOfCases > 0 ? ` (${numberOfCases})` : ''}`,
            `Files${numberOfCases > 0 ? ` (${numberOfFiles})` : ''}`,
          ]}
          contents={[
            <ObjectCases object={id} pageBasedPagination={true} />,
            <ObjectFiles
              object={id}
              noRecordsToDisplay={
                <div className='slds-m-left_large'>
                  <SvgIcon
                    variant='utility'
                    symbol='info'
                    colour='light'
                    size='small'
                    className='slds-m-right_x-small'
                  />
                  No related items found
                </div>
              }
            />
          ]}
          tabSpacing='x-large'
          selectedTab={this.props.openTab}
          onClick={this.props.onTabClick}
          tabsClassName='slds-p-left_large slds-p-bottom_small slds-p-right_large'
        />
        {edit &&
          <Mutation mutation={MUTATION_WITHDRAWAL}>
            {withdrawalMutate => {
              const targetType = targetProduct
                ? 'product'
                : 'bankaccount'

              const targetId = targetProduct
                ? targetProduct.id
                : targetBankaccount.id

              return (
                <EditModal
                  sourceName={account.description}
                  targetType={targetType}
                  targetId={targetId}
                  initiateOn={initiateOn}
                  amountRequested={amountRequested}
                  amountType={amountType}
                  withdrawableAccounts={client.withdrawableAccounts}
                  withdrawableTargetProducts={client.withdrawableTargetProducts}
                  withdrawableTargetBankaccounts={client.withdrawableTargetBankaccounts}
                  userMayOverrideDate={userMayOverrideDate}
                  onCancelClick={this.toggleEditModal}
                  onSaveClick={(accountId, targetType, targetId, initiateOn, amountRequested, amountType, productMetaDataObj) => {
                    this.saveChanges(withdrawalMutate, accountId, targetType, targetId, initiateOn, amountRequested, amountType, productMetaDataObj)
                  }}
                />
              )
            }}
          </Mutation>
        }
        {cancel &&
          <Mutation mutation={MUTATION_WITHDRAWAL}>
            {withdrawalMutate => (
              <CancelModal
                onCancelClick={this.toggleCancelModal}
                onConfirmClick={cancelledBy => this.saveCancellation(withdrawalMutate, cancelledBy)}
              />
            )}
          </Mutation>
        }
        {bankChange &&
          <Mutation mutation={WITHDRAWAL_BANKACCOUNT_UPDATE}>
            {bankaccountUpdate => {
              const targetBankaccountId = targetBankaccount.id

              return (
                <BankChangeModal
                  targetBankaccountId={targetBankaccountId}
                  withdrawableTargetBankaccounts={client.withdrawableTargetBankaccounts}
                  onCancelClick={this.toggleBankChangeModal}
                  onSaveClick={(targetBankaccountId) => {
                    this.saveBankChange(bankaccountUpdate, targetBankaccountId)
                  }}
                />
              )
            }}
          </Mutation>
        }
        {showModalErrorMessage &&
          <Modal
            ref={modalRef}
            body={
              <div className='slds-text-align_center'>
                <div>
                  <SvgIcon
                    variant='utility'
                    symbol='warning'
                    colour='error'
                    size='medium'
                  />
                </div>
                <p className='slds-m-top_medium slds-p-around_small u-font-size-larger'>
                  {/*
                    the returned error string is in the form of "'[message]'", so we'll just
                    grab the actual string we want displayed, without the brackets and single quotes
                  */}
                  {showModalErrorMessage.substring(2,showModalErrorMessage.length-2)}
                </p>
              </div>
            }
          />
        }
      </div>
    )
  }
}



// WEBPACK FOOTER //
// ./src/components/withdrawal-detail/index.js